import { useEffect, useState } from "react";
import {
  Users, Stethoscope, FlaskConical, Clock, Car, Plane, AlertTriangle,
  BellRing, CheckCircle2, Loader2, Info, Banknote,
} from "lucide-react";
import { api } from "@/lib/api";
import { STAGE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};
const apptLabel = (t) =>
  !t ? "Appointment" : t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const SUB_STATUS_META = {
  in_treatment: { key: "inTreatment", cls: "text-blue-700 bg-blue-100" },
  waiting_for_dentist: { key: "waitingForDentist", cls: "text-amber-700 bg-amber-100" },
  visit_completed: { key: "visitCompleted", cls: "text-emerald-700 bg-emerald-100" },
};
const subStatusMeta = (s) => SUB_STATUS_META[s] || { key: "arrived", cls: "text-emerald-700 bg-emerald-100" };

function Tile({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = {
    slate: "text-slate-500",
    blue: "text-blue-600",
    amber: "text-amber-600",
    green: "text-emerald-600",
    red: "text-red-600",
  };
  return (
    <div data-testid={testid} className="stat-lift bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} strokeWidth={2} />
      </div>
      <p className="font-heading text-3xl font-semibold text-slate-900 mt-2 tabular-nums">{value}</p>
    </div>
  );
}

export default function OperationsDashboard() {
  const { user } = useAuth();
  const tr = useT();
  const [data, setData] = useState(null);
  const [arrivals, setArrivals] = useState([]);
  const [overdueIds, setOverdueIds] = useState(new Set());
  const [threshold, setThreshold] = useState(null);
  const [thresholdDraft, setThresholdDraft] = useState("");
  const canEditThreshold = ["owner", "operations_manager"].includes(user?.role);

  useEffect(() => {
    api.get("/dashboard/operations").then((r) => setData(r.data)).catch(() => {});
    api.get("/appointments/waiting-config")
      .then((r) => { setThreshold(r.data.threshold_minutes); setThresholdDraft(String(r.data.threshold_minutes)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadArrivals = () => {
      api.get("/patients/arrivals/log").then((r) => setArrivals(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      api.get("/appointments/waiting-alerts")
        .then((r) => setOverdueIds(new Set((r.data || []).map((a) => a.appointment_id))))
        .catch(() => {});
    };
    loadArrivals();
    const t = setInterval(loadArrivals, 20000);
    return () => clearInterval(t);
  }, []);

  const saveThreshold = () => {
    const n = Number(thresholdDraft);
    if (!n || n < 1 || n > 240) return;
    api.post("/appointments/waiting-config", { threshold_minutes: n })
      .then((r) => setThreshold(r.data.threshold_minutes)).catch(() => {});
  };

  if (!data)
    return (
      <div className="flex items-center justify-center h-full p-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );

  const t = data.tiles;
  const stages = Object.entries(data.stage_counts).filter(([, v]) => v > 0);

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={tr("dashboard.opsTitle")} subtitle={tr("dashboard.opsSubtitle")}>
        {data.synthetic ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-semibold">
            <Info className="h-3.5 w-3.5" /> {tr("dashboard.synthetic")}
          </span>
        ) : (
          <span data-testid="ops-live-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {tr("dashboard.live")}
          </span>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Tile testid="tile-in-clinic" icon={Users} tone="blue" label={tr("ops.inClinic")} value={t.in_clinic} />
        <Tile testid="tile-awaiting" icon={Clock} tone="amber" label={tr("ops.awaiting")} value={t.awaiting_examination} />
        <Tile testid="tile-treatment" icon={Stethoscope} tone="blue" label={tr("ops.inTreatment")} value={t.in_treatment} />
        <Tile testid="tile-lab" icon={FlaskConical} tone="blue" label={tr("ops.labInProduction")} value={t.laboratory_in_production} />
        <Tile testid="tile-lab-delays" icon={AlertTriangle} tone="red" label={tr("ops.labDelays")} value={t.laboratory_delays} />
        <Tile testid="tile-ready" icon={CheckCircle2} tone="green" label={tr("ops.readyForPickup")} value={t.ready_for_pickup} />
        <Tile testid="tile-transit" icon={Car} tone="blue" label={tr("ops.inTransit")} value={t.in_transit} />
        <Tile testid="tile-awaiting-transport" icon={Clock} tone="amber" label={tr("ops.awaitingTransport")} value={t.awaiting_transport} />
        <Tile testid="tile-flights" icon={Plane} label={tr("ops.upcomingFlights")} value={t.upcoming_flights} />
        <Tile testid="tile-conflicts" icon={AlertTriangle} tone="red" label={tr("ops.travelConflicts")} value={t.travel_conflicts} />
        <Tile testid="tile-completed-trips" icon={CheckCircle2} tone="green" label={tr("ops.completedTrips")} value={t.completed_trips_today} />
        <Tile testid="tile-cash-disc" icon={Banknote} tone="red" label={tr("ops.cashDiscrepancies")} value={t.cash_discrepancies} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{tr("ops.journeyDistribution")}</h3>
          <div className="space-y-3">
            {stages.map(([stage, count]) => {
              const total = data.tiles.total_patients || 1;
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className="w-48 text-sm text-slate-600 shrink-0">{tr(`stages.${stage}`, STAGE_LABELS[stage] || stage)}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(count / total) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-sm font-medium text-slate-900 tabular-nums">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col" data-testid="arrival-log-card">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-medium text-slate-900">{tr("ops.arrivalLog")}</h3>
            <div className="flex items-center gap-2">
              {canEditThreshold ? (
                <div className="flex items-center gap-1" data-testid="waiting-threshold-editor">
                  <span className="text-xs text-slate-400">{tr("ops.alert")} &gt;</span>
                  <Input type="number" min={1} max={240} value={thresholdDraft}
                    onChange={(e) => setThresholdDraft(e.target.value)}
                    className="h-7 w-16 text-xs" data-testid="waiting-threshold-input" />
                  <span className="text-xs text-slate-400">{tr("ops.min")}</span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={saveThreshold} data-testid="waiting-threshold-save">{tr("common.save")}</Button>
                </div>
              ) : (
                threshold != null && <span className="text-xs text-slate-400" data-testid="waiting-threshold-label">{tr("ops.alert")} &gt; {threshold} {tr("ops.min")}</span>
              )}
              <span className="text-xs text-slate-400">{tr("common.today")}</span>
            </div>
          </div>
          {arrivals.length === 0 ? (
            <div className="flex-1 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-center p-6" data-testid="arrival-log-empty">
              <BellRing className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-sm font-medium text-slate-600">{tr("ops.noArrivals")}</p>
              <p className="text-xs text-slate-400 mt-1">{tr("ops.arrivalsHint")}</p>
            </div>
          ) : (
            <div className="space-y-2 overflow-y-auto max-h-[420px]" data-testid="arrival-log-list">
              {arrivals.map((a) => {
                const meta = subStatusMeta(a.sub_status);
                const overdue = overdueIds.has(a.appointment_id);
                return (
                <div key={a.id} data-testid={`arrival-row-${a.id}`} className={`rounded-lg border px-3 py-2.5 ${overdue ? "border-red-300 bg-red-50/70" : "border-slate-100 bg-slate-50/60"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">{a.patient_name}</p>
                    <span data-testid={`arrival-substatus-${a.id}`} className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${meta.cls}`}>
                      {a.sub_status === "in_treatment" ? <Stethoscope className="h-3 w-3" /> : a.sub_status === "waiting_for_dentist" ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} {tr(`ops.${meta.key}`)}
                    </span>
                  </div>
                  {overdue && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1" data-testid={`overdue-flag-${a.id}`}>
                      <AlertTriangle className="h-3 w-3" /> {tr("ops.overdue")}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1 truncate">
                      <Stethoscope className="h-3 w-3 shrink-0" />
                      {a.appointment_time
                        ? `${fmtTime(a.appointment_time)} · ${apptLabel(a.appointment_type)}${a.dentist_name ? ` · ${a.dentist_name}` : ""}`
                        : tr("ops.noAppointment")}
                    </span>
                    <span className="flex items-center gap-1 shrink-0 ml-2">
                      <Clock className="h-3 w-3" /> {fmtTime(a.arrived_at)}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {a.source === "manual_checkin" ? tr("ops.manualCheckin") : tr("ops.driverDelivery")}{a.recorded_by ? ` · ${a.recorded_by}` : ""}
                    {a.sub_status === "in_treatment" && a.treatment_started_at ? ` · In treatment ${fmtTime(a.treatment_started_at)}` : ""}
                    {a.sub_status === "visit_completed" && a.visit_completed_at ? ` · Completed ${fmtTime(a.visit_completed_at)}` : ""}
                  </p>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
